import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { HealthIssueDetails } from '@ideals/services/health-checker';
import { ClipboardDirective } from '@ideals/ui-components/clipboard';
import { PageMessagesService } from '@ideals/ui-components/page-messages';
import { TooltipDirective } from '@ideals/ui-components/tooltip';

import { TroubleshootingStatus } from '../../services/troubleshooting.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.failed]': 'status() === "failed"',
  },
  imports: [
    ClipboardDirective,
    NgTemplateOutlet,
    TooltipDirective,
    TranslatePipe,
  ],
  selector: 'ideals-help-troubleshooting',
  styleUrl: './help-troubleshooting.component.scss',
  templateUrl: './help-troubleshooting.component.html',
})
export class HelpTroubleshootingComponent {
  readonly issues = input.required<HealthIssueDetails[]>();
  readonly status = input.required<TroubleshootingStatus>();

  readonly downloadReport = output();
  readonly runDiagnostic = output();

  readonly #pageMessages = inject(PageMessagesService);
  readonly #translateService = inject(TranslateService);

  protected readonly issuesText = computed(() => this.#stringifyIssues());

  protected onCopyToClipboard(): void {
    const text = this.#translateService.instant('common.TOASTER.copied_to_clipboard') as string;

    this.#pageMessages.success({ text });
  }

  #stringifyIssues(): string {
    return this.issues().map((issue) => {
      const details = issue.details
        ?.replace(/\s+/g, '')
        ?.replace(/;/g, '\n') ?? '';

      return [
        this.#translateService.instant(issue.title),
        this.#translateService.instant(issue.description),
        details,
        issue.cta ? this.#translateService.instant(issue.cta) : null,
      ]
        .filter((v) => v)
        .join('\n');
    }).join('\n\n');
  }
}
